import { useEffect, useState, useRef } from "react";
import { Link as ScrollLink } from "react-scroll";
import CountUp from "react-countup";
import {
  FaStar, FaWhatsapp, FaPhone, FaEnvelope, FaMapMarkerAlt,
  FaSwimmingPool, FaFire, FaUtensils, FaVolleyballBall, FaWifi,
  FaCar, FaMusic, FaSpa, FaArrowUp, FaBars, FaTimes, FaCalendarAlt,
  FaUsers, FaLeaf, FaShieldAlt, FaMoneyBillWave, FaClock, FaChevronDown,
} from "react-icons/fa";

// ─── SEO HEAD MANAGER ──────────────────────────────────────────
function useSEO({ title, description, canonical, keywords, ogImage }) {
  useEffect(() => {
    document.title = title;
    const setMeta = (name, content, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", description);
    setMeta("keywords", keywords);
    setMeta("robots", "index, follow");
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", window.location.href, true);
    if (ogImage) setMeta("og:image", ogImage, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", canonical || window.location.href);
  }, [title, description, canonical, keywords, ogImage]);
}

// ─── STRUCTURED DATA ──────────────────────────────────────────
function useStructuredData(data) {
  useEffect(() => {
    const id = "structured-data-json-ld";
    let el = document.getElementById(id);
    if (!el) { el = document.createElement("script"); el.id = id; el.type = "application/ld+json"; document.head.appendChild(el); }
    el.textContent = JSON.stringify(data);
    return () => el.remove();
  }, []);
}

// ─── DATA ─────────────────────────────────────────────────────
const farmhouses = [
  { name: "Royal Palm Estate", img: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?q=80&w=2070", price: "₹45,000/night", rating: 4.9, guests: "12 Guests • 6 Bedrooms", features: ["Private Pool", "Luxury Spa", "Bonfire Area", "BBQ Zone"] },
  { name: "Lakeview Paradise", img: "https://images.unsplash.com/photo-1507089947368-19c1da9775ae?q=80&w=2070", price: "₹38,000/night", rating: 4.8, guests: "10 Guests • 5 Bedrooms", features: ["Lake View", "Fishing Dock", "Kayaking", "Sunset Deck"] },
  { name: "Emerald Greens", img: "https://images.unsplash.com/photo-1572120360610-d971b9d7767c?q=80&w=2070", price: "₹52,000/night", rating: 5.0, guests: "15 Guests • 7 Bedrooms", features: ["Golf Course", "Wine Cellar", "Home Theater", "Chef Kitchen"] },
  { name: "Mountain Retreat", img: "https://images.unsplash.com/photo-1518780664697-55e3ad937233?q=80&w=2065", price: "₹42,000/night", rating: 4.7, guests: "8 Guests • 4 Bedrooms", features: ["Mountain View", "Hiking Trails", "Hot Tub", "Fireplace"] },
  { name: "Sunset Vineyard", img: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?q=80&w=2070", price: "₹65,000/night", rating: 4.9, guests: "20 Guests • 8 Bedrooms", features: ["Wine Tasting", "Vineyard Tour", "Banquet Hall", "Pool Bar"] },
  { name: "Riverfront Haven", img: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=2070", price: "₹35,000/night", rating: 4.6, guests: "6 Guests • 3 Bedrooms", features: ["River Access", "Floating Deck", "Water Sports", "Picnic Area"] },
];

const amenities = [
  { icon: <FaSwimmingPool />, title: "Infinity Pools", desc: "Temperature-controlled luxury pools" },
  { icon: <FaFire />, title: "Bonfire Nights", desc: "Cozy fire pits with seating" },
  { icon: <FaUtensils />, title: "Gourmet Dining", desc: "Private chefs & farm-to-table meals" },
  { icon: <FaVolleyballBall />, title: "Sports Arena", desc: "Badminton, volleyball & more" },
  { icon: <FaWifi />, title: "High-Speed WiFi", desc: "Seamless connectivity throughout" },
  { icon: <FaCar />, title: "Valet Parking", desc: "Secure parking for all guests" },
  { icon: <FaMusic />, title: "Sound Systems", desc: "Premium indoor/outdoor audio" },
  { icon: <FaSpa />, title: "Wellness Spa", desc: "Ayurvedic treatments & massages" },
];

const testimonials = [
  { name: "Rajesh Mehta", role: "Wedding Planner", text: "Organized a destination wedding for 150 guests. Flawless execution and stunning venues!", rating: 5 },
  { name: "Priya Sharma", role: "Corporate Event Manager", text: "Our team retreat was transformative. The amenities and service exceeded expectations.", rating: 5 },
  { name: "Arun Patel", role: "Family Vacationer", text: "Three generations celebrated together. Memories created will last a lifetime.", rating: 4.8 },
];

const FAQ_DATA = [
  { q: "Is the farmhouse couple friendly?", a: "Yes, V Farmstays is a completely couple friendly farmhouse in Moinabad. We offer full privacy, secure premises, and a peaceful atmosphere for couples seeking a quiet getaway." },
  { q: "Do you have a private swimming pool?", a: "Yes! Our farmhouse with pool in Moinabad Hyderabad features a clean, private swimming pool exclusively reserved for your group during your stay — no shared access with other guests." },
  { q: "Can I host a birthday party or event here?", a: "Absolutely. Our farmhouse for party in Moinabad can accommodate up to 200 guests. We offer sound systems, open lawns, lighting, and catering support to make your event memorable." },
  { q: "How far is V Farmstays from Hyderabad?", a: "V Farmstays is located in Moinabad, approximately 25–30 km from central Hyderabad — a convenient 45-minute drive, perfect for a weekend farmhouse getaway." },
  { q: "Is it a budget-friendly farmhouse?", a: "Yes! We offer the best budget farmhouse in Moinabad Hyderabad. Our pricing includes premium amenities, pool access, and full privacy at competitive rates. Contact us for custom packages." },
  { q: "Do you allow overnight stays?", a: "Yes, overnight stays are welcome. We are one of the top weekend farmhouses in Moinabad Hyderabad and cater to both day-use and overnight bookings for families, couples, and groups." },
];

// ─── FAQ ACCORDION ────────────────────────────────────────────
function FAQSection() {
  const [open, setOpen] = useState(null);
  return (
    <section id="faq" className="vf-section" aria-label="Frequently Asked Questions">
      <div className="container">
        <div className="text-center mb-5">
          <span className="vf-badge">FAQ</span>
          <h2 className="vf-heading mt-2">Frequently Asked Questions</h2>
          <p className="vf-subtext mx-auto mt-3" style={{ maxWidth: 600 }}>
            Everything you need to know about booking a farmhouse stay in Moinabad, Hyderabad.
          </p>
        </div>
        <div className="row justify-content-center">
          <div className="col-lg-8">
            {FAQ_DATA.map((item, i) => (
              <div key={i} className="vf-faq-item mb-3" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <button
                  className="vf-faq-btn w-100 d-flex justify-content-between align-items-center"
                  onClick={() => setOpen(open === i ? null : i)}
                  aria-expanded={open === i}
                >
                  <span itemProp="name" className="text-start">{item.q}</span>
                  <FaChevronDown className={`vf-faq-icon ${open === i ? "open" : ""}`} />
                </button>
                {open === i && (
                  <div className="vf-faq-body" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p itemProp="text" className="mb-0">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── HOME PAGE ────────────────────────────────────────────────
function HomePage({ closeMobileMenu }) {
  useSEO({
    title: "V Farmstays | Best Private Farmhouse in Moinabad Hyderabad | Pool, Party & Weekend Stays",
    description: "Book the best private farmhouse in Moinabad Hyderabad. Budget farmhouse with private pool, couple friendly, perfect for parties, weekend getaways & family stays. Call +91 9133294999.",
    canonical: "https://www.vfarmstays.com/",
    keywords: "farmhouse in Moinabad Hyderabad, private farmhouse Moinabad, farmhouse with pool Moinabad, budget farmhouse Moinabad, couple friendly farmhouse Moinabad, weekend farmhouse Hyderabad, farmhouse for party Moinabad, farm stay Moinabad Telangana",
    ogImage: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?q=80&w=1200",
  });

  useStructuredData({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LodgingBusiness",
        "@id": "https://www.vfarmstays.com/#business",
        "name": "V Farmstays",
        "description": "Luxury private farmhouse in Moinabad Hyderabad with pool, ideal for parties, weekend getaways, and family staycations.",
        "url": "https://www.vfarmstays.com",
        "telephone": "+919133294999",
        "email": "bookings@vfarmstays.com",
        "address": { "@type": "PostalAddress", "addressLocality": "Moinabad", "addressRegion": "Telangana", "addressCountry": "IN" },
        "geo": { "@type": "GeoCoordinates", "latitude": "17.2800", "longitude": "78.2200" },
        "amenityFeature": [
          { "@type": "LocationFeatureSpecification", "name": "Private Swimming Pool", "value": true },
          { "@type": "LocationFeatureSpecification", "name": "Bonfire Area", "value": true },
          { "@type": "LocationFeatureSpecification", "name": "WiFi", "value": true },
          { "@type": "LocationFeatureSpecification", "name": "Parking", "value": true },
          { "@type": "LocationFeatureSpecification", "name": "Gourmet Dining", "value": true },
        ],
        "priceRange": "₹₹₹",
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "500", "bestRating": "5" },
        "openingHours": "Mo-Su 00:00-24:00",
        "hasMap": "https://maps.google.com/?q=Moinabad+Hyderabad",
        "sameAs": ["https://wa.me/919133294999"],
      },
      {
        "@type": "FAQPage",
        "mainEntity": FAQ_DATA.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a }
        }))
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.vfarmstays.com/" }]
      }
    ]
  });

  return (
    <>
      {/* HERO */}
      <section id="home" className="vf-hero" aria-label="V Farmstays – Private Farmhouse in Moinabad Hyderabad">
        <div className="vf-hero-bg" aria-hidden="true" />
        <div className="container vf-hero-content">
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <span className="vf-badge animate-fade-in">LUXURY NATURE RETREATS · MOINABAD, HYDERABAD</span>
              <h1 className="vf-hero-title animate-slide-up mt-3">
                Book the Perfect<br />
                <span className="vf-text-gradient">Private Farmhouse</span><br />
                in Moinabad Hyderabad
              </h1>
              <p className="vf-hero-sub animate-fade-in-delay mx-auto mt-4">
                Escape city chaos at V Farmstays — a budget farmhouse in Moinabad with a private pool.
                Ideal for family staycations, weekend getaways, couple retreats &amp; private parties.
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center mt-5 animate-fade-in-delay2">
                <a href="https://wa.me/919133294999" target="_blank" rel="noopener noreferrer"
                  className="vf-btn-primary" aria-label="Book farmhouse via WhatsApp">
                  <FaWhatsapp className="me-2" aria-hidden="true" /> Book Your Stay on WhatsApp
                </a>
                <a href="tel:+919133294999" className="vf-btn-outline" aria-label="Call V Farmstays">
                  <FaPhone className="me-2" aria-hidden="true" /> +91 9133294999
                </a>
              </div>
            </div>
          </div>

          <div className="row g-4 mt-5">
            {[
              { icon: "🎯", title: "Custom Events", desc: "Tailored to your needs" },
              { icon: "🛡️", title: "24/7 Security", desc: "Complete privacy assured" },
              { icon: "👨‍🍳", title: "Private Chef", desc: "Gourmet dining experience" },
              { icon: "🌿", title: "Nature Escape", desc: "Lush green surroundings" },
            ].map((item, i) => (
              <div className="col-sm-6 col-lg-3" key={i}>
                <div className="vf-card text-center h-100">
                  <div className="vf-card-icon">{item.icon}</div>
                  <h3 className="vf-card-title">{item.title}</h3>
                  <p className="vf-card-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <a href="#about" className="vf-scroll-hint" aria-label="Scroll down">
          <FaChevronDown />
        </a>
      </section>

      {/* ABOUT */}
      <section id="about" className="vf-section vf-section-alt" aria-label="About V Farmstays">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <img src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?q=80&w=2070"
                alt="V Farmstays luxury farmhouse property in Moinabad Hyderabad with pool and green lawn"
                className="vf-img-rounded" loading="lazy" width="600" height="400" />
            </div>
            <div className="col-lg-6">
              <span className="vf-badge">ABOUT US</span>
              <h2 className="vf-heading mt-2">Welcome to V Farmstays – Your Ultimate Weekend Farmhouse in Moinabad, Hyderabad</h2>
              <p className="vf-body mt-4">
                Looking for a peaceful and refreshing <strong>farm stay near Moinabad Hyderabad</strong>? V Farmstays is your
                ultimate nature retreat located right in Moinabad, Telangana. Designed to give you a perfect blend of rustic charm
                and modern amenities, we are widely recognised as a top <strong>farm stay in Moinabad Telangana</strong>.
              </p>
              <p className="vf-body mt-3">
                Whether you need a completely <strong>private farmhouse in Moinabad Hyderabad</strong> to relax with your family,
                or a spacious <strong>farmhouse for party in Moinabad</strong> to celebrate birthdays and anniversaries, we cater
                to all needs with luxury at budget-friendly prices.
              </p>
              <div className="d-flex gap-4 mt-4 flex-wrap">
                {[["🏡", "Est. 2015"], ["⭐", "4.9 Rating"], ["🎉", "500+ Events"], ["🔒", "100% Private"]].map(([icon, label]) => (
                  <div key={label} className="vf-stat-pill"><span>{icon}</span> {label}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="vf-stats-section" aria-label="V Farmstays achievements">
        <div className="container">
          <div className="row text-center g-4">
            {[
              { end: 500, suffix: "+", label: "Happy Guests" },
              { end: 50, suffix: "+", label: "Luxury Properties" },
              { end: 4.9, suffix: "", decimals: 1, label: "Guest Rating" },
              { end: 100, suffix: "+", label: "Events Hosted" },
            ].map((stat, i) => (
              <div className="col-6 col-lg-3" key={i}>
                <div className="vf-stat-block">
                  <div className="vf-stat-number">
                    <CountUp end={stat.end} duration={4} decimals={stat.decimals || 0} />{stat.suffix}
                  </div>
                  <p className="vf-stat-label">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="vf-section" aria-label="Why choose V Farmstays">
        <div className="container">
          <div className="text-center mb-5">
            <span className="vf-badge">WHY CHOOSE US</span>
            <h2 className="vf-heading mt-2">Why V Farmstays is the Best Farmhouse for Rent in Moinabad?</h2>
          </div>
          <div className="row g-4">
            {[
              { icon: <FaSwimmingPool />, title: "Premium Farmhouse with Pool in Moinabad Hyderabad", desc: "Dive into pure relaxation! Our property features a clean, well-maintained private swimming pool perfect for daytime fun or evening pool parties." },
              { icon: <FaShieldAlt />, title: "Safe & Couple Friendly Farmhouse in Moinabad", desc: "We respect your privacy. Enjoy a fully secure, peaceful, and absolutely private environment designed for couples looking for a quiet getaway." },
              { icon: <FaMoneyBillWave />, title: "Best Budget Farmhouse in Moinabad Hyderabad", desc: "Luxury doesn't have to be expensive. Experience premium comfort, lush green lawns, and top-tier amenities at highly affordable prices." },
              { icon: <FaClock />, title: "The Ideal Weekend Farmhouse in Moinabad Hyderabad", desc: "Conveniently located near the city yet miles away from the noise — the perfect spot for weekend relaxation and family outings." },
            ].map((item, i) => (
              <div className="col-md-6 col-lg-3" key={i}>
                <article className="vf-feature-card h-100">
                  <div className="vf-feature-icon" aria-hidden="true">{item.icon}</div>
                  <h3 className="vf-feature-title">{item.title}</h3>
                  <p className="vf-card-desc">{item.desc}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FARMHOUSES */}
      <section id="farmhouses" className="vf-section vf-section-alt" aria-label="Featured farmhouse properties">
        <div className="container">
          <div className="text-center mb-5">
            <span className="vf-badge">EXCLUSIVE COLLECTION</span>
            <h2 className="vf-heading mt-2">Featured Farmhouses in Moinabad Hyderabad</h2>
            <p className="vf-subtext mx-auto mt-3" style={{ maxWidth: 600 }}>
              Discover our curated luxury properties, each offering unique experiences and premium amenities near Hyderabad.
            </p>
          </div>
          <div className="row g-4">
            {farmhouses.map((house, i) => (
              <div className="col-md-6 col-lg-4" key={i}>
                <article className="vf-property-card h-100" itemScope itemType="https://schema.org/LodgingBusiness">
                  <div className="vf-property-img-wrap">
                    <img src={house.img} alt={`${house.name} – luxury farmhouse near Moinabad Hyderabad`}
                      className="vf-property-img" loading="lazy" width="600" height="400" />
                    <span className="vf-property-price">{house.price}</span>
                  </div>
                  <div className="vf-property-body">
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <h3 className="vf-property-title mb-0" itemProp="name">{house.name}</h3>
                      <div className="vf-rating" aria-label={`Rating: ${house.rating}`}>
                        <FaStar aria-hidden="true" /> <span itemProp="aggregateRating">{house.rating}</span>
                      </div>
                    </div>
                    <p className="vf-property-guests" itemProp="description">{house.guests}</p>
                    <div className="d-flex flex-wrap gap-2 mb-4">
                      {house.features.map((feature, idx) => (
                        <span key={idx} className="vf-feature-tag">{feature}</span>
                      ))}
                    </div>
                    <a href="https://wa.me/919133294999" target="_blank" rel="noopener noreferrer"
                      className="vf-btn-primary w-100 text-center d-block"
                      aria-label={`Enquire about ${house.name}`}>
                      Enquire on WhatsApp
                    </a>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AMENITIES */}
      <section id="amenities" className="vf-section" aria-label="Farmhouse amenities and facilities">
        <div className="container">
          <div className="text-center mb-5">
            <span className="vf-badge">PREMIUM FACILITIES</span>
            <h2 className="vf-heading mt-2">World-Class Amenities at Our Moinabad Farmhouse</h2>
            <p className="vf-subtext mx-auto mt-3" style={{ maxWidth: 600 }}>
              Every property is equipped with luxury amenities to ensure an exceptional farm stay experience.
            </p>
          </div>
          <div className="row g-4">
            {amenities.map((item, i) => (
              <div className="col-sm-6 col-lg-3" key={i}>
                <div className="vf-amenity-card text-center h-100">
                  <div className="vf-amenity-icon" aria-hidden="true">{item.icon}</div>
                  <h3 className="vf-feature-title">{item.title}</h3>
                  <p className="vf-card-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="vf-section vf-section-alt" aria-label="Guest reviews and testimonials">
        <div className="container">
          <div className="text-center mb-5">
            <span className="vf-badge">HAPPY CLIENTS</span>
            <h2 className="vf-heading mt-2">Guest Reviews – Farmhouse in Moinabad Hyderabad</h2>
          </div>
          <div className="row g-4">
            {testimonials.map((t, i) => (
              <div className="col-md-4" key={i}>
                <article className="vf-testimonial-card h-100" itemScope itemType="https://schema.org/Review">
                  <div className="vf-stars mb-3" aria-label={`${t.rating} out of 5 stars`}>
                    {[...Array(5)].map((_, idx) => <FaStar key={idx} aria-hidden="true" />)}
                    <span className="ms-2 vf-rating-text">{t.rating}</span>
                  </div>
                  <blockquote className="vf-quote" itemProp="reviewBody">"{t.text}"</blockquote>
                  <div itemProp="author" itemScope itemType="https://schema.org/Person">
                    <p className="vf-author-name mb-0" itemProp="name">{t.name}</p>
                    <small className="vf-author-role">{t.role}</small>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection />

      {/* CONTACT */}
      <section id="contact" className="vf-section vf-section-alt" aria-label="Contact V Farmstays">
        <div className="container">
          <div className="row g-5">
            <div className="col-lg-6">
              <span className="vf-badge">GET IN TOUCH</span>
              <h2 className="vf-heading mt-2">Plan Your Perfect Farmhouse Escape in Moinabad</h2>
              <p className="vf-body mt-3">
                Our team is ready to help you book the perfect farmhouse stay in Moinabad, Hyderabad. Get in touch for personalised
                packages, group bookings, and event planning assistance.
              </p>
              <address className="d-flex flex-column gap-4 mt-4" style={{ fontStyle: "normal" }}>
                {[
                  { icon: <FaPhone />, label: "Call Us", val: "+91 9133294999", href: "tel:+919133294999" },
                  { icon: <FaWhatsapp />, label: "WhatsApp", val: "+91 9133294999", href: "https://wa.me/919133294999" },
                  { icon: <FaEnvelope />, label: "Email", val: "bookings@vfarmstays.com", href: "mailto:bookings@vfarmstays.com" },
                  { icon: <FaMapMarkerAlt />, label: "Location", val: "Moinabad, Hyderabad, Telangana – 500075", href: null },
                ].map((c, i) => (
                  <div className="d-flex align-items-center gap-3" key={i}>
                    <div className="vf-contact-icon-wrap" aria-hidden="true">{c.icon}</div>
                    <div>
                      <small className="vf-contact-label">{c.label}</small>
                      {c.href
                        ? <p className="fw-bold mb-0"><a href={c.href} className="text-white text-decoration-none">{c.val}</a></p>
                        : <p className="fw-bold mb-0">{c.val}</p>}
                    </div>
                  </div>
                ))}
              </address>
            </div>
            <div className="col-lg-6">
              <div className="vf-contact-form-wrap">
                <h3 className="vf-feature-title mb-4">Send Us a Booking Enquiry</h3>
                <form name="farmstays-enquiry" method="POST" action="#contact">
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <label htmlFor="contact-name" className="visually-hidden">Your Name</label>
                      <input id="contact-name" type="text" name="name" className="vf-input" placeholder="Your Name" required />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="contact-phone" className="visually-hidden">Phone Number</label>
                      <input id="contact-phone" type="tel" name="phone" className="vf-input" placeholder="Phone Number" required />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="contact-email" className="visually-hidden">Email Address</label>
                    <input id="contact-email" type="email" name="email" className="vf-input" placeholder="Email Address" />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="contact-type" className="visually-hidden">Property Type</label>
                    <select id="contact-type" name="type" className="vf-input">
                      <option value="">Select Stay Type</option>
                      <option value="party">Farmhouse for Party</option>
                      <option value="weekend">Weekend Getaway</option>
                      <option value="pool">Pool Farmhouse</option>
                      <option value="couple">Couple Friendly Stay</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-msg" className="visually-hidden">Message</label>
                    <textarea id="contact-msg" name="message" rows="4" className="vf-input" placeholder="Tell us about your celebration or stay requirements…"></textarea>
                  </div>
                  <button type="submit" className="vf-btn-primary w-100">Send Enquiry</button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ─── PARTY VENUE PAGE ─────────────────────────────────────────
function PartyVenuePage() {
  useSEO({
    title: "Farmhouse for Party in Moinabad Hyderabad | Birthday, Events & Night Parties – V Farmstays",
    description: "Host the perfect party at V Farmstays. Best farmhouse for party in Moinabad Hyderabad with huge lawn, private pool, sound system & overnight stay. Up to 200 guests. Call +91 9133294999.",
    canonical: "https://www.vfarmstays.com/farmhouse-for-party-in-moinabad",
    keywords: "farmhouse for party in Moinabad, party farmhouse Moinabad Hyderabad, birthday party farmhouse Hyderabad, night party farmhouse Moinabad, event venue Moinabad, corporate event farmhouse Hyderabad",
    ogImage: "https://images.unsplash.com/photo-1464366400600-7168b4e0d19e?q=80&w=1200",
  });

  useStructuredData({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "EventVenue",
        "name": "V Farmstays – Farmhouse for Party in Moinabad",
        "description": "Premier farmhouse event venue in Moinabad Hyderabad for birthday parties, corporate events, and gatherings up to 200 guests.",
        "url": "https://www.vfarmstays.com/farmhouse-for-party-in-moinabad",
        "telephone": "+919133294999",
        "address": { "@type": "PostalAddress", "addressLocality": "Moinabad", "addressRegion": "Telangana", "addressCountry": "IN" },
        "maximumAttendeeCapacity": 200,
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.vfarmstays.com/" },
          { "@type": "ListItem", "position": 2, "name": "Farmhouse for Party in Moinabad", "item": "https://www.vfarmstays.com/farmhouse-for-party-in-moinabad" }
        ]
      }
    ]
  });

  return (
    <div className="vf-page-wrapper">
      <div className="container py-5">
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb vf-breadcrumb">
            <li className="breadcrumb-item"><a href="/" onClick={e => { e.preventDefault(); window.history.pushState({}, "", "/"); window.location.reload(); }}>Home</a></li>
            <li className="breadcrumb-item active" aria-current="page">Farmhouse for Party in Moinabad</li>
          </ol>
        </nav>

        <div className="row g-5">
          <div className="col-lg-8">
            <span className="vf-badge">EVENT VENUE</span>
            <h1 className="vf-heading mt-2">Spacious Farmhouse for Party in Moinabad – Host Your Special Events</h1>
            <p className="vf-body lead mt-4">
              Planning a <strong>birthday party</strong>, <strong>family reunion</strong>, corporate get-together, or <strong>night party</strong>?
              V Farmstays is the premier <strong>farmhouse for party in Moinabad</strong>. Our property features expansive manicured lawns,
              open-air setup, and complete privacy — party your way without city restrictions.
            </p>
            <img src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?q=80&w=1200"
              alt="Birthday and night party at farmhouse in Moinabad Hyderabad – V Farmstays event venue"
              className="vf-img-rounded my-4 w-100" loading="lazy" width="800" height="500" />

            <h2 className="vf-subheading">Perfect Venue for Night Parties &amp; Family Gatherings in Moinabad</h2>
            <p className="vf-body mt-3">
              Our <strong>private farmhouse in Moinabad Hyderabad</strong> is fully equipped for medium to large gatherings.
              From beautiful lighting to professional sound setups, we make every event a grand success.
            </p>

            <div className="row g-4 mt-3">
              {[
                { icon: <FaUsers />, title: "Up to 200 Guests", sub: "Spacious event capacity" },
                { icon: <FaMusic />, title: "Sound System Ready", sub: "Professional audio setup" },
                { icon: <FaSwimmingPool />, title: "Pool Access", sub: "Private pool for pool parties" },
                { icon: <FaFire />, title: "Bonfire Area", sub: "Cozy night events" },
                { icon: <FaUtensils />, title: "Catering Available", sub: "Customised menus" },
                { icon: <FaShieldAlt />, title: "24/7 Security", sub: "Complete safety" },
              ].map((item, i) => (
                <div className="col-md-4 col-sm-6" key={i}>
                  <div className="vf-feature-card text-center p-3 h-100">
                    <div className="vf-feature-icon mx-auto" aria-hidden="true">{item.icon}</div>
                    <h3 className="vf-card-title mt-2">{item.title}</h3>
                    <p className="vf-card-desc mb-0">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="vf-subheading mt-5">Why Choose Our Farmhouse for Your Next Party in Moinabad?</h2>
            <ul className="vf-checklist mt-3">
              <li>Fully <strong>private farmhouse party venue</strong> — no strangers, no restrictions</li>
              <li>Large manicured lawn suitable for <strong>outdoor parties up to 200 guests</strong></li>
              <li>Private <strong>swimming pool</strong> available for pool parties</li>
              <li>Professional <strong>sound system</strong> with outdoor speakers</li>
              <li>Overnight stays available — perfect for <strong>all-night celebrations</strong></li>
              <li>Only 30 minutes from central Hyderabad — <strong>easy access from Gachibowli, Tolichowki, Jubilee Hills</strong></li>
            </ul>
          </div>

          <div className="col-lg-4">
            <div className="vf-sticky-form">
              <h3 className="vf-feature-title mb-4">Check Party Availability</h3>
              <form name="party-enquiry" method="POST" action="#party-form">
                <div className="mb-3">
                  <label htmlFor="party-name" className="visually-hidden">Your Name</label>
                  <input id="party-name" type="text" name="name" className="vf-input" placeholder="Your Name" required />
                </div>
                <div className="mb-3">
                  <label htmlFor="party-phone" className="visually-hidden">Phone Number</label>
                  <input id="party-phone" type="tel" name="phone" className="vf-input" placeholder="Phone Number" required />
                </div>
                <div className="mb-3">
                  <label htmlFor="party-date" className="visually-hidden">Event Date</label>
                  <input id="party-date" type="date" name="date" className="vf-input" />
                </div>
                <div className="mb-3">
                  <label htmlFor="party-guests" className="visually-hidden">Number of Guests</label>
                  <select id="party-guests" name="guests" className="vf-input">
                    <option value="">Number of Guests</option>
                    <option>50–100</option>
                    <option>100–150</option>
                    <option>150–200</option>
                  </select>
                </div>
                <button type="submit" className="vf-btn-primary w-100">
                  <FaCalendarAlt className="me-2" aria-hidden="true" /> Check Party Availability
                </button>
              </form>
              <div className="mt-4 pt-3 vf-form-divider">
                <a href="tel:+919133294999" className="vf-btn-outline w-100 text-center d-block">
                  <FaPhone className="me-2" aria-hidden="true" /> Call for Quick Booking
                </a>
                <a href="https://wa.me/919133294999" target="_blank" rel="noopener noreferrer" className="vf-btn-whatsapp w-100 text-center d-block mt-2">
                  <FaWhatsapp className="me-2" aria-hidden="true" /> Chat on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── STAYCATION PAGE ─────────────────────────────────────────
function StaycationPage() {
  useSEO({
    title: "Farm Stay in Moinabad Telangana | Best Weekend Farmhouse Near Hyderabad – V Farmstays",
    description: "Enjoy the best farm stay in Moinabad Telangana at V Farmstays. Weekend farmhouse near Hyderabad with private pool, lush greenery & modern amenities. Book now at +91 9133294999.",
    canonical: "https://www.vfarmstays.com/farm-stay-in-moinabad-telangana",
    keywords: "farm stay in Moinabad Telangana, weekend farmhouse Moinabad Hyderabad, farm stay near Hyderabad, nature retreat Moinabad, weekend getaway Hyderabad farmhouse, digital detox Moinabad",
    ogImage: "https://images.unsplash.com/photo-1542718610-a1d656d1884c?q=80&w=1200",
  });

  useStructuredData({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Resort",
        "name": "V Farmstays – Farm Stay in Moinabad Telangana",
        "description": "Premium farm stay near Moinabad Hyderabad for weekend getaways. Lush greenery, private pool, and modern amenities.",
        "url": "https://www.vfarmstays.com/farm-stay-in-moinabad-telangana",
        "telephone": "+919133294999",
        "address": { "@type": "PostalAddress", "addressLocality": "Moinabad", "addressRegion": "Telangana", "addressCountry": "IN" },
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.vfarmstays.com/" },
          { "@type": "ListItem", "position": 2, "name": "Farm Stay in Moinabad Telangana", "item": "https://www.vfarmstays.com/farm-stay-in-moinabad-telangana" }
        ]
      }
    ]
  });

  return (
    <div className="vf-page-wrapper">
      <div className="container py-5">
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb vf-breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active" aria-current="page">Farm Stay in Moinabad Telangana</li>
          </ol>
        </nav>

        <div className="row g-5">
          <div className="col-lg-8">
            <span className="vf-badge">WEEKEND GETAWAY</span>
            <h1 className="vf-heading mt-2">Premium Farm Stay in Moinabad Telangana for Weekend Getaways</h1>
            <p className="vf-body lead mt-4">
              Ditch the crowded resorts and experience nature at its best. V Farmstays is a highly-rated <strong>farm stay near Moinabad
                Hyderabad</strong> that offers a peaceful escape into nature. If you are searching for a serene <strong>weekend farmhouse in
                  Moinabad Hyderabad</strong>, our property blends rustic countryside vibes with modern home comforts.
            </p>
            <img src="https://images.unsplash.com/photo-1542718610-a1d656d1884c?q=80&w=2070"
              alt="Nature farm stay near Moinabad Hyderabad – V Farmstays weekend getaway with lush greenery"
              className="vf-img-rounded my-4 w-100" loading="lazy" width="800" height="500" />

            <h2 className="vf-subheading">Experience a Refreshing Farm Stay Near Moinabad Hyderabad</h2>
            <p className="vf-body mt-3">
              Wake up to birds chirping and enjoy uninterrupted views of lush greenery. Whether you want to read under a tree or
              relax by the pool, this is the finest <strong>farm stay in Moinabad Telangana</strong> for a complete digital detox.
            </p>

            <div className="row g-4 mt-3">
              {[
                { icon: <FaLeaf />, title: "Lush Greenery", desc: "Surrounded by nature's finest flora" },
                { icon: <FaClock />, title: "Peaceful Environment", desc: "Complete digital detox experience" },
                { icon: <FaSwimmingPool />, title: "Private Pool", desc: "Cool off in your exclusive pool" },
                { icon: <FaUtensils />, title: "Farm-to-Table Dining", desc: "Fresh meals in a natural setting" },
              ].map((item, i) => (
                <div className="col-md-6" key={i}>
                  <div className="d-flex gap-3 align-items-start vf-feature-card p-3">
                    <div className="vf-feature-icon flex-shrink-0 small" aria-hidden="true">{item.icon}</div>
                    <div>
                      <h3 className="vf-card-title mb-1">{item.title}</h3>
                      <p className="vf-card-desc mb-0">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="vf-subheading mt-5">Ideal Stays at Our Weekend Farmhouse Near Hyderabad</h2>
            <ul className="vf-checklist mt-3">
              <li><strong>Family weekend getaways</strong> — spacious, safe, and fully private</li>
              <li><strong>Couple romantic retreats</strong> — peaceful, private, and intimate</li>
              <li><strong>Friends weekend trips</strong> — pool, games, bonfire, and more</li>
              <li><strong>Solo digital detox</strong> — surrounded by nature and silence</li>
              <li>Only <strong>30–45 min drive from Hyderabad</strong> — Gachibowli, HITEC City, Jubilee Hills</li>
            </ul>
          </div>

          <div className="col-lg-4">
            <div className="vf-sticky-form">
              <h3 className="vf-feature-title mb-4">Book Your Weekend Staycation</h3>
              <form name="staycation-enquiry" method="POST" action="#staycation-form">
                <div className="mb-3">
                  <input type="text" name="name" className="vf-input" placeholder="Your Name" required />
                </div>
                <div className="mb-3">
                  <input type="tel" name="phone" className="vf-input" placeholder="Phone Number" required />
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label htmlFor="checkin-stay" className="visually-hidden">Check In</label>
                    <input id="checkin-stay" type="date" name="checkin" className="vf-input" placeholder="Check In" />
                  </div>
                  <div className="col-6">
                    <label htmlFor="checkout-stay" className="visually-hidden">Check Out</label>
                    <input id="checkout-stay" type="date" name="checkout" className="vf-input" placeholder="Check Out" />
                  </div>
                </div>
                <div className="mb-3">
                  <select name="guests" className="vf-input">
                    <option value="">Number of Guests</option>
                    <option>2–4 Guests</option>
                    <option>4–8 Guests</option>
                    <option>8–12 Guests</option>
                  </select>
                </div>
                <button type="submit" className="vf-btn-primary w-100">
                  <FaCalendarAlt className="me-2" aria-hidden="true" /> Book Your Weekend Staycation
                </button>
              </form>
              <div className="mt-4 pt-3 vf-form-divider">
                <a href="tel:+919133294999" className="vf-btn-outline w-100 text-center d-block">
                  <FaPhone className="me-2" aria-hidden="true" /> Call for Best Rates
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── POOL FARMHOUSE PAGE ───────────────────────────────────────
function PoolFarmhousePage() {
  useSEO({
    title: "Farmhouse with Pool in Moinabad Hyderabad | Couple Friendly Budget Stay – V Farmstays",
    description: "Book a private farmhouse with pool in Moinabad Hyderabad at V Farmstays. Couple friendly, budget-friendly, 100% private pool villa. Best rates guaranteed. Call +91 9133294999.",
    canonical: "https://www.vfarmstays.com/farmhouse-with-pool-in-moinabad-hyderabad",
    keywords: "farmhouse with pool in Moinabad Hyderabad, private pool farmhouse Moinabad, couple friendly farmhouse Moinabad, budget farmhouse with pool Hyderabad, swimming pool farmhouse Moinabad",
    ogImage: "https://images.unsplash.com/photo-1560743641-3914f2c45636?q=80&w=1200",
  });

  useStructuredData({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "VacationRental",
        "name": "V Farmstays – Farmhouse with Pool in Moinabad Hyderabad",
        "description": "Private farmhouse with pool in Moinabad Hyderabad. Couple friendly and budget-friendly with 100% exclusive pool access.",
        "url": "https://www.vfarmstays.com/farmhouse-with-pool-in-moinabad-hyderabad",
        "telephone": "+919133294999",
        "address": { "@type": "PostalAddress", "addressLocality": "Moinabad", "addressRegion": "Telangana", "addressCountry": "IN" },
        "amenityFeature": [
          { "@type": "LocationFeatureSpecification", "name": "Private Swimming Pool", "value": true },
          { "@type": "LocationFeatureSpecification", "name": "Couple Friendly", "value": true },
          { "@type": "LocationFeatureSpecification", "name": "Air Conditioned Rooms", "value": true },
        ],
        "priceRange": "₹₹",
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.vfarmstays.com/" },
          { "@type": "ListItem", "position": 2, "name": "Farmhouse with Pool in Moinabad Hyderabad", "item": "https://www.vfarmstays.com/farmhouse-with-pool-in-moinabad-hyderabad" }
        ]
      }
    ]
  });

  return (
    <div className="vf-page-wrapper">
      <div className="container py-5">
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb vf-breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active" aria-current="page">Farmhouse with Pool in Moinabad Hyderabad</li>
          </ol>
        </nav>

        <div className="row g-5">
          <div className="col-lg-8">
            <span className="vf-badge">PRIVATE POOL</span>
            <h1 className="vf-heading mt-2">Luxury Farmhouse with Pool in Moinabad Hyderabad – V Farmstays</h1>
            <p className="vf-body lead mt-4">
              Beat the Hyderabad heat in your own private oasis! V Farmstays features a crystal-clear, deep <strong>farmhouse with pool
                in Moinabad Hyderabad</strong>. Unlike public resorts, our swimming pool is entirely private — reserved exclusively for
              you and your guests during your stay.
            </p>
            <img src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?q=80&w=1200"
              alt="Private swimming pool at V Farmstays – couple friendly budget farmhouse in Moinabad Hyderabad"
              className="vf-img-rounded my-4 w-100" loading="lazy" width="800" height="500" />

            <h2 className="vf-subheading">Couple Friendly &amp; Budget Farmhouse in Moinabad Hyderabad</h2>
            <p className="vf-body mt-3">
              We strictly prioritise safety and exclusivity, making V Farmstays a top choice for those seeking a <strong>couple
                friendly farmhouse in Moinabad</strong>. Get premium pool access, air-conditioned rooms, and absolute security at
              the most competitive rates as the <strong>best budget farmhouse in Moinabad Hyderabad</strong>.
            </p>

            <div className="row g-4 mt-3">
              {[
                { icon: <FaSwimmingPool />, title: "Private Pool", sub: "Exclusively for your group" },
                { icon: <FaShieldAlt />, title: "Couple Friendly", sub: "Complete privacy & security" },
                { icon: <FaMoneyBillWave />, title: "Budget Friendly", sub: "Best rates in Moinabad" },
                { icon: <FaClock />, title: "Flexible Timing", sub: "Day & overnight bookings" },
              ].map((item, i) => (
                <div className="col-md-6 col-sm-6" key={i}>
                  <div className="vf-feature-card text-center p-4 h-100">
                    <div className="vf-feature-icon mx-auto" aria-hidden="true">{item.icon}</div>
                    <h3 className="vf-card-title mt-2">{item.title}</h3>
                    <p className="vf-card-desc mb-0">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="vf-subheading mt-5">Why Book Our Private Pool Farmhouse in Moinabad?</h2>
            <ul className="vf-checklist mt-3">
              <li><strong>100% private pool</strong> — no strangers, no shared access</li>
              <li>Clean and maintained <strong>swimming pool</strong> with safety measures</li>
              <li>Perfect for <strong>couples, families, and small groups</strong></li>
              <li><strong>Air-conditioned rooms</strong> for comfortable overnight stays</li>
              <li>Affordable <strong>budget farmhouse</strong> pricing with no compromise on quality</li>
              <li>25–30 km from Hyderabad — accessible from <strong>Tolichowki, Attapur, Rajendra Nagar</strong></li>
            </ul>
          </div>

          <div className="col-lg-4">
            <div className="vf-sticky-form">
              <h3 className="vf-feature-title mb-4">Book Pool Farmhouse Now</h3>
              <form name="pool-enquiry" method="POST" action="#pool-form">
                <div className="mb-3">
                  <input type="text" name="name" className="vf-input" placeholder="Your Name" required />
                </div>
                <div className="mb-3">
                  <input type="tel" name="phone" className="vf-input" placeholder="Phone Number" required />
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label htmlFor="checkin-pool" className="visually-hidden">Check In</label>
                    <input id="checkin-pool" type="date" name="checkin" className="vf-input" />
                  </div>
                  <div className="col-6">
                    <label htmlFor="checkout-pool" className="visually-hidden">Check Out</label>
                    <input id="checkout-pool" type="date" name="checkout" className="vf-input" />
                  </div>
                </div>
                <div className="mb-3">
                  <select name="guests" className="vf-input">
                    <option value="">Number of Guests</option>
                    <option>2–4 Guests</option>
                    <option>4–8 Guests</option>
                    <option>8–12 Guests</option>
                  </select>
                </div>
                <button type="submit" className="vf-btn-primary w-100">
                  <FaSwimmingPool className="me-2" aria-hidden="true" /> Book Pool Farmhouse Now
                </button>
              </form>
              <div className="mt-4 pt-3 vf-form-divider">
                <a href="tel:+919133294999" className="vf-btn-outline w-100 text-center d-block">
                  <FaPhone className="me-2" aria-hidden="true" /> Call for Pool Booking
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── FOOTER ───────────────────────────────────────────────────
function Footer({ navigateTo }) {
  return (
    <footer className="vf-footer" role="contentinfo">
      <div className="container">
        <div className="row g-4">
          <div className="col-sm-6 col-lg-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <div className="vf-logo-circle" aria-hidden="true"><span>V</span></div>
              <span className="vf-logo-text">V Farmstays</span>
            </div>
            <p className="vf-footer-text">The best private farmhouse in Moinabad, Hyderabad — luxury nature retreats since 2015.</p>
          </div>
          <div className="col-sm-6 col-lg-3">
            <h2 className="vf-footer-heading">Quick Links</h2>
            <ul className="list-unstyled vf-footer-links">
              <li><a href="/" onClick={e => { e.preventDefault(); navigateTo("/", "home"); }}>Home</a></li>
              <li><a href="/farmhouse-for-party-in-moinabad" onClick={e => { e.preventDefault(); navigateTo("/farmhouse-for-party-in-moinabad", "party"); }}>Farmhouse for Party in Moinabad</a></li>
              <li><a href="/farm-stay-in-moinabad-telangana" onClick={e => { e.preventDefault(); navigateTo("/farm-stay-in-moinabad-telangana", "staycation"); }}>Farm Stay in Moinabad</a></li>
              <li><a href="/farmhouse-with-pool-in-moinabad-hyderabad" onClick={e => { e.preventDefault(); navigateTo("/farmhouse-with-pool-in-moinabad-hyderabad", "pool"); }}>Farmhouse with Pool – Moinabad</a></li>
            </ul>
          </div>
          <div className="col-sm-6 col-lg-3">
            <h2 className="vf-footer-heading">Contact</h2>
            <address style={{ fontStyle: "normal" }}>
              <ul className="list-unstyled vf-footer-text">
                <li className="mb-2"><a href="tel:+919133294999" className="text-decoration-none">📞 +91 9133294999</a></li>
                <li className="mb-2"><a href="https://wa.me/919133294999" className="text-decoration-none" target="_blank" rel="noopener noreferrer">💬 WhatsApp Us</a></li>
                <li className="mb-2"><a href="mailto:bookings@vfarmstays.com" className="text-decoration-none">✉️ bookings@vfarmstays.com</a></li>
                <li>📍 Moinabad, Hyderabad, Telangana – 500075</li>
              </ul>
            </address>
          </div>
          <div className="col-sm-6 col-lg-3">
            <h2 className="vf-footer-heading">Newsletter</h2>
            <p className="vf-footer-text mb-3">Subscribe for exclusive offers on farmhouse stays near Hyderabad.</p>
            <form name="newsletter" onSubmit={e => e.preventDefault()}>
              <label htmlFor="newsletter-email" className="visually-hidden">Email address</label>
              <div className="d-flex gap-2">
                <input id="newsletter-email" type="email" name="email" className="vf-input flex-grow-1" placeholder="Your email" />
                <button type="submit" className="vf-btn-primary px-3" aria-label="Subscribe to newsletter">→</button>
              </div>
            </form>
          </div>
        </div>
        <hr className="vf-footer-hr my-4" />
        <p className="text-center vf-footer-text small mb-0">
          © 2026 V Farmstays — Luxury <a href="/" className="text-decoration-none">Farmhouse in Moinabad Hyderabad</a>. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

// ─── APP ──────────────────────────────────────────────────────
export default function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState("home");

  useEffect(() => {
    const path = window.location.pathname;
    if (path === "/farmhouse-for-party-in-moinabad") setCurrentPage("party");
    else if (path === "/farm-stay-in-moinabad-telangana") setCurrentPage("staycation");
    else if (path === "/farmhouse-with-pool-in-moinabad-hyderabad") setCurrentPage("pool");
    else setCurrentPage("home");
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 300);
      const h = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      setScrollProgress(h ? (document.documentElement.scrollTop / h) * 100 : 0);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = (path, page) => {
    window.history.pushState({}, "", path);
    setCurrentPage(page);
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <div className="vf-app">
      {/* SCROLL PROGRESS */}
      <div className="vf-progress-bar" style={{ width: `${scrollProgress}%` }} aria-hidden="true" />

      {/* NAV */}
      <nav className={`vf-nav ${isScrolled ? "vf-nav-scrolled" : ""}`} role="navigation" aria-label="Main navigation">
        <div className="container d-flex align-items-center justify-content-between py-2">
          <a
            className="
    vf-navbar-brand
    d-flex
    align-items-center
    gap-2
    text-decoration-none
  "
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigateTo("/", "home");
            }}
            aria-label="V Farmstays Home"
          >

            {/* Logo */}
            <div
              className="
      vf-logo-circle
      d-flex
      align-items-center
      justify-content-center
      flex-shrink-0
    "
              style={{
                width: "46px",
                height: "46px",
                borderRadius: "14px",
                background: "rgba(255,255,255,0.12)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "0 8px 30px rgba(0,0,0,0.12)"
              }}
              aria-hidden="true"
            >

              <img
                src="/logo.png"
                alt="V Farmstays Logo"
                className="object-contain"
                style={{
                  width: "34px",
                  height: "34px"
                }}
              />

            </div>

            {/* Text */}
            <div className="d-flex flex-column lh-sm">

              <span
                className="vf-logo-text"
                style={{
                  fontSize: "1.1rem",
                  fontWeight: "700",
                  color: "#ffffff",
                  letterSpacing: "-0.02em"
                }}
              >
                V Farmstays
              </span>

              <span
                style={{
                  fontSize: "10px",
                  color: "rgba(255,255,255,0.65)",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  marginTop: "2px"
                }}
              >
                Luxury Farm Stay
              </span>

            </div>

          </a>

          <button className="vf-menu-toggle d-lg-none" type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-expanded={isMobileMenuOpen} aria-controls="vf-nav-menu"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}>
            {isMobileMenuOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
          </button>

          <div id="vf-nav-menu" className={`vf-nav-menu ${isMobileMenuOpen ? "open" : ""}`}>
            <ul className="list-unstyled d-flex flex-column flex-lg-row align-items-lg-center gap-1 gap-lg-2 mb-0">
              {currentPage === "home" ? (
                ["home", "farmhouses", "amenities", "contact"].map(item => (
                  <li key={item}>
                    <ScrollLink to={item} smooth duration={600} className="vf-nav-link" onClick={closeMobileMenu}>
                      {item.toUpperCase()}
                    </ScrollLink>
                  </li>
                ))
              ) : (
                [
                  { path: "/", page: "home", label: "HOME" },
                  { path: "/farmhouse-for-party-in-moinabad", page: "party", label: "PARTY VENUE" },
                  { path: "/farm-stay-in-moinabad-telangana", page: "staycation", label: "WEEKEND STAY" },
                  { path: "/farmhouse-with-pool-in-moinabad-hyderabad", page: "pool", label: "POOL FARMHOUSE" },
                ].map(link => (
                  <li key={link.page}>
                    <a href={link.path} className={`vf-nav-link ${currentPage === link.page ? "active" : ""}`}
                      onClick={e => { e.preventDefault(); navigateTo(link.path, link.page); }}>
                      {link.label}
                    </a>
                  </li>
                ))
              )}
            </ul>
            <a href="https://wa.me/919133294999" target="_blank" rel="noopener noreferrer"
              className="vf-btn-primary ms-lg-3 mt-3 mt-lg-0 d-flex align-items-center gap-2"
              onClick={closeMobileMenu}
              aria-label="Book your farmhouse stay on WhatsApp">
              <FaWhatsapp aria-hidden="true" /> Book Now
            </a>
          </div>
        </div>
      </nav>

      {/* PAGES */}
      <main id="main-content">
        {currentPage === "home" && <HomePage closeMobileMenu={closeMobileMenu} />}
        {currentPage === "party" && <PartyVenuePage />}
        {currentPage === "staycation" && <StaycationPage />}
        {currentPage === "pool" && <PoolFarmhousePage />}
      </main>

      <Footer navigateTo={navigateTo} />

      {/* FLOATING WHATSAPP */}
      <a href="https://wa.me/919133294999" target="_blank" rel="noopener noreferrer"
        className="vf-float-wa" aria-label="Chat with V Farmstays on WhatsApp">
        <FaWhatsapp size={26} aria-hidden="true" />
      </a>

      {/* SCROLL TO TOP */}
      {showScrollTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="vf-scroll-top" aria-label="Scroll to top">
          <FaArrowUp aria-hidden="true" />
        </button>
      )}

      <style>{`
        /* ── TOKENS ── */
        :root {
          --vf-red: #E8322A;
          --vf-red-dark: #C0251E;
          --vf-red-glow: rgba(232,50,42,0.25);
          --vf-bg: #0D0D0D;
          --vf-bg2: #141414;
          --vf-bg3: #1A1A1A;
          --vf-border: rgba(255,255,255,0.08);
          --vf-text: #F5F5F5;
          --vf-muted: rgba(255,255,255,0.5);
          --vf-font-display: 'Georgia', 'Times New Roman', serif;
          --vf-font-body: -apple-system, 'Segoe UI', sans-serif;
          --vf-radius: 16px;
          --vf-transition: 0.25s ease;
        }

        /* ── RESET ── */
        .vf-app { background: var(--vf-bg); color: var(--vf-text); font-family: var(--vf-font-body); overflow-x: hidden; }
        *, *::before, *::after { box-sizing: border-box; }

        /* ── PROGRESS BAR ── */
        .vf-progress-bar { position: fixed; top: 0; left: 0; height: 3px; background: var(--vf-red); z-index: 9999; transition: width 0.1s linear; }

        /* ── TYPOGRAPHY ── */
        .vf-heading { font-family: var(--vf-font-display); font-size: clamp(1.8rem, 4vw, 2.8rem); font-weight: 700; line-height: 1.15; color: var(--vf-text); }
        .vf-hero-title { font-family: var(--vf-font-display); font-size: clamp(2.2rem, 5.5vw, 4rem); font-weight: 700; line-height: 1.1; color: var(--vf-text); }
        .vf-subheading { font-size: 1.4rem; font-weight: 600; color: var(--vf-text); margin-top: 2rem; }
        .vf-body { color: var(--vf-muted); line-height: 1.75; }
        .vf-subtext { color: var(--vf-muted); }
        .vf-text-gradient { background: linear-gradient(135deg, #FF5A5F, #FF9A59); -webkit-background-clip: text; background-clip: text; color: transparent; }
        .vf-badge { display: inline-block; font-size: 0.7rem; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--vf-red); background: rgba(232,50,42,0.12); border: 1px solid rgba(232,50,42,0.3); border-radius: 100px; padding: 4px 14px; }

        /* ── NAVBAR ── */
        .vf-nav { position: fixed; top: 0; left: 0; right: 0; z-index: 1000; padding: 0.5rem 0; transition: background var(--vf-transition), backdrop-filter var(--vf-transition), border-bottom var(--vf-transition); }
        .vf-nav-scrolled { background: rgba(13,13,13,0.95); backdrop-filter: blur(12px); border-bottom: 1px solid var(--vf-red); }
        .vf-logo-circle { width: 38px; height: 38px; background: var(--vf-red); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 1rem; flex-shrink: 0; }
        .vf-logo-text { font-size: 1.2rem; font-weight: 700; color: var(--vf-text); font-family: var(--vf-font-display); }
        .vf-navbar-brand { text-decoration: none; }
        .vf-nav-link { display: inline-block; color: #fff; text-decoration: none; font-size: 0.78rem; font-weight: 600; letter-spacing: 0.5px; padding: 6px 12px; border-radius: 8px; transition: color var(--vf-transition), background var(--vf-transition); cursor: pointer; }
        .vf-nav-link:hover, .vf-nav-link.active { color: var(--vf-text); background: rgba(255,255,255,0.06); }
        .vf-menu-toggle { background: none; border: 1px solid var(--vf-border); color: var(--vf-text); border-radius: 8px; padding: 6px 10px; cursor: pointer; }
        @media (max-width: 991px) {
          .vf-nav-menu { position: absolute; top: 100%; left: 0; right: 0; background: rgba(13,13,13,0.98); backdrop-filter: blur(20px); border-bottom: 1px solid var(--vf-border); padding: 1rem; display: none; }
          .vf-nav-menu.open { display: block; }
        }

        /* ── HERO ── */
        .vf-hero { position: relative; min-height: 100vh; display: flex; align-items: center; padding: 100px 0 60px; overflow: hidden; }
        .vf-hero-bg { position: absolute; inset: 0; background: radial-gradient(ellipse 80% 60% at 50% 20%, rgba(232,50,42,0.12) 0%, transparent 70%), linear-gradient(180deg, rgba(13,13,13,0) 0%, rgba(13,13,13,0.6) 100%), url('https://images.unsplash.com/photo-1564013799919-ab600027ffc6?q=80&w=2070') center/cover no-repeat; z-index: 0; }
        .vf-hero-content { position: relative; z-index: 1; }
        .vf-hero-sub { font-size: 1.1rem; color: rgba(255,255,255,0.65); max-width: 680px; line-height: 1.7; }
        .vf-scroll-hint { position: absolute; bottom: 2rem; left: 50%; transform: translateX(-50%); color: var(--vf-muted); font-size: 1.2rem; text-decoration: none; animation: bounce 2s infinite; }
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(8px); } }

        /* ── SECTIONS ── */
        .vf-section { padding: 80px 0; }
        .vf-section-alt { background: var(--vf-bg2); }
        .vf-stats-section { padding: 60px 0; background: linear-gradient(135deg, var(--vf-bg3) 0%, var(--vf-bg2) 100%); border-top: 1px solid var(--vf-border); border-bottom: 1px solid var(--vf-border); }
        .vf-page-wrapper { padding-top: 80px; min-height: 100vh; }

        /* ── CARDS ── */
        .vf-card { background: rgba(255,255,255,0.04); border: 1px solid var(--vf-border); border-radius: var(--vf-radius); padding: 2rem; transition: border-color var(--vf-transition), transform var(--vf-transition); }
        .vf-card:hover { border-color: rgba(232,50,42,0.3); transform: translateY(-4px); }
        .vf-card-icon { font-size: 2.5rem; margin-bottom: 1rem; }
        .vf-card-title { font-size: 1rem; font-weight: 700; color: var(--vf-text); margin-bottom: 0.25rem; }
        .vf-card-desc { font-size: 0.85rem; color: var(--vf-muted); margin-bottom: 0; }

        .vf-feature-card { background: rgba(255,255,255,0.03); border: 1px solid var(--vf-border); border-radius: var(--vf-radius); padding: 1.75rem; transition: border-color var(--vf-transition), background var(--vf-transition); }
        .vf-feature-card:hover { border-color: rgba(232,50,42,0.4); background: rgba(232,50,42,0.05); }
        .vf-feature-icon { color: var(--vf-red); font-size: 2rem; margin-bottom: 0.75rem; }
        .vf-feature-title { font-size: 0.95rem; font-weight: 700; color: var(--vf-text); margin-bottom: 0.5rem; }

        .vf-amenity-card { background: rgba(255,255,255,0.03); border: 1px solid var(--vf-border); border-radius: var(--vf-radius); padding: 2rem; transition: border-color var(--vf-transition), transform var(--vf-transition); }
        .vf-amenity-card:hover { border-color: var(--vf-red); transform: translateY(-4px); }
        .vf-amenity-icon { font-size: 2rem; color: var(--vf-red); margin-bottom: 1rem; }

        .vf-property-card { background: var(--vf-bg3); border: 1px solid var(--vf-border); border-radius: var(--vf-radius); overflow: hidden; transition: border-color var(--vf-transition), transform var(--vf-transition); }
        .vf-property-card:hover { border-color: rgba(232,50,42,0.4); transform: translateY(-6px); }
        .vf-property-img-wrap { position: relative; overflow: hidden; }
        .vf-property-img { width: 100%; height: 240px; object-fit: cover; display: block; transition: transform 0.4s ease; }
        .vf-property-card:hover .vf-property-img { transform: scale(1.04); }
        .vf-property-price { position: absolute; top: 12px; right: 12px; background: var(--vf-red); color: #fff; font-size: 0.8rem; font-weight: 700; border-radius: 100px; padding: 5px 14px; }
        .vf-property-body { padding: 1.25rem; }
        .vf-property-title { font-size: 1.05rem; font-weight: 700; }
        .vf-property-guests { font-size: 0.82rem; color: var(--vf-muted); margin-bottom: 0.75rem; }
        .vf-rating { color: #FFB800; font-size: 0.85rem; display: flex; align-items: center; gap: 4px; }
        .vf-feature-tag { font-size: 0.72rem; font-weight: 600; background: rgba(232,50,42,0.12); color: var(--vf-red); border: 1px solid rgba(232,50,42,0.25); border-radius: 100px; padding: 3px 10px; }

        .vf-testimonial-card { background: rgba(255,255,255,0.03); border: 1px solid var(--vf-border); border-radius: var(--vf-radius); padding: 1.75rem; }
        .vf-stars { color: #FFB800; font-size: 0.85rem; }
        .vf-rating-text { color: var(--vf-text); font-weight: 600; font-size: 0.85rem; }
        .vf-quote { font-style: italic; color: rgba(255,255,255,0.75); margin-bottom: 1rem; line-height: 1.7; font-size: 0.9rem; }
        .vf-author-name { font-weight: 700; font-size: 0.95rem; }
        .vf-author-role { color: var(--vf-red); font-size: 0.8rem; }

        /* ── STATS ── */
        .vf-stat-block { padding: 1.5rem; }
        .vf-stat-number { font-family: var(--vf-font-display); font-size: clamp(2rem, 5vw, 3rem); font-weight: 700; color: var(--vf-red); }
        .vf-stat-label { font-size: 0.72rem; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--vf-muted); margin-bottom: 0; }
        .vf-stat-pill { display: flex; align-items: center; gap: 6px; background: rgba(255,255,255,0.05); border: 1px solid var(--vf-border); border-radius: 100px; padding: 6px 14px; font-size: 0.82rem; font-weight: 600; }

        /* ── BUTTONS ── */
        .vf-btn-primary { display: inline-flex; align-items: center; justify-content: center; background: var(--vf-red); color: #fff; font-size: 0.88rem; font-weight: 700; border: none; border-radius: 100px; padding: 12px 28px; cursor: pointer; text-decoration: none; transition: background var(--vf-transition), box-shadow var(--vf-transition), transform var(--vf-transition); }
        .vf-btn-primary:hover { background: var(--vf-red-dark); box-shadow: 0 0 20px var(--vf-red-glow); transform: translateY(-2px); color: #fff; }
        .vf-btn-outline { display: inline-flex; align-items: center; justify-content: center; background: transparent; color: var(--vf-text); font-size: 0.88rem; font-weight: 700; border: 1.5px solid rgba(255,255,255,0.25); border-radius: 100px; padding: 12px 28px; cursor: pointer; text-decoration: none; transition: border-color var(--vf-transition), background var(--vf-transition); }
        .vf-btn-outline:hover { border-color: var(--vf-red); color: var(--vf-red); background: rgba(232,50,42,0.06); }
        .vf-btn-whatsapp { display: inline-flex; align-items: center; justify-content: center; background: #25D366; color: #fff; font-size: 0.88rem; font-weight: 700; border: none; border-radius: 100px; padding: 12px 28px; cursor: pointer; text-decoration: none; transition: opacity var(--vf-transition); }
        .vf-btn-whatsapp:hover { opacity: 0.9; color: #fff; }

        /* ── FORMS ── */
        .vf-input { width: 100%; background: rgba(255,255,255,0.06); border: 1px solid var(--vf-border); border-radius: 10px; padding: 12px 16px; color: var(--vf-text); font-size: 0.88rem; outline: none; transition: border-color var(--vf-transition); }
        .vf-input:focus { border-color: var(--vf-red); box-shadow: 0 0 0 3px rgba(232,50,42,0.12); }
        .vf-input::placeholder { color: var(--vf-muted); }
        .vf-input option { background: #1a1a1a; color: var(--vf-text); }
        .vf-sticky-form { background: rgba(255,255,255,0.03); border: 1px solid var(--vf-border); border-radius: var(--vf-radius); padding: 2rem; position: sticky; top: 100px; }
        .vf-contact-form-wrap { background: rgba(255,255,255,0.03); border: 1px solid var(--vf-border); border-radius: var(--vf-radius); padding: 2rem; }
        .vf-form-divider { border-top: 1px solid var(--vf-border); }
        .vf-contact-icon-wrap { background: rgba(232,50,42,0.12); color: var(--vf-red); border-radius: 10px; padding: 0.75rem; font-size: 1.1rem; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .vf-contact-label { font-size: 0.72rem; color: var(--vf-muted); letter-spacing: 0.5px; display: block; }

        /* ── FAQ ── */
        .vf-faq-item { background: rgba(255,255,255,0.03); border: 1px solid var(--vf-border); border-radius: 12px; overflow: hidden; }
        .vf-faq-btn { background: transparent; border: none; color: var(--vf-text); padding: 1.1rem 1.5rem; font-size: 0.95rem; font-weight: 600; cursor: pointer; transition: background var(--vf-transition); }
        .vf-faq-btn:hover { background: rgba(255,255,255,0.03); }
        .vf-faq-icon { color: var(--vf-red); transition: transform 0.25s ease; }
        .vf-faq-icon.open { transform: rotate(180deg); }
        .vf-faq-body { padding: 0 1.5rem 1.25rem; color: var(--vf-muted); font-size: 0.9rem; line-height: 1.75; border-top: 1px solid var(--vf-border); margin-top: 0; padding-top: 1rem; }

        /* ── MISC ── */
        .vf-img-rounded { border-radius: var(--vf-radius); width: 100%; height: auto; display: block; }
        .vf-checklist { list-style: none; padding: 0; }
        .vf-checklist li { padding: 0.4rem 0 0.4rem 2rem; position: relative; color: var(--vf-muted); font-size: 0.92rem; line-height: 1.6; }
        .vf-checklist li::before { content: "✓"; position: absolute; left: 0; color: var(--vf-red); font-weight: 700; }
        .vf-checklist strong { color: var(--vf-text); }

        /* ── BREADCRUMB ── */
        .vf-breadcrumb { background: transparent; padding: 0; font-size: 0.82rem; }
        .vf-breadcrumb .breadcrumb-item + .breadcrumb-item::before { color: var(--vf-muted); }
        .vf-breadcrumb a { color: var(--vf-red); text-decoration: none; }
        .vf-breadcrumb .active { color: var(--vf-muted); }

        /* ── FOOTER ── */
        .vf-footer { background: var(--vf-bg2); border-top: 1px solid var(--vf-border); padding: 60px 0 40px; }
        .vf-footer-heading { font-size: 0.85rem; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--vf-text); margin-bottom: 1rem; }
        .vf-footer-text { font-size: 0.85rem; color: var(--vf-muted); }
        .vf-footer-links { font-size: 0.85rem; }
        .vf-footer-links li { margin-bottom: 0.5rem; }
        .vf-footer-links a { color: var(--vf-muted); text-decoration: none; transition: color var(--vf-transition); }
        .vf-footer-links a:hover { color: var(--vf-red); }
        .vf-footer-hr { border-color: var(--vf-border); }
        .vf-footer address a { color: var(--vf-muted) !important; }
        .vf-footer address a:hover { color: var(--vf-red) !important; }

        /* ── FLOATING BUTTONS ── */
        .vf-float-wa { position: fixed; bottom: 24px; right: 24px; z-index: 999; width: 54px; height: 54px; background: #25D366; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; box-shadow: 0 4px 20px rgba(37,211,102,0.4); transition: transform var(--vf-transition); }
        .vf-float-wa:hover { transform: scale(1.1); color: #fff; }
        .vf-scroll-top { position: fixed; bottom: 88px; right: 24px; z-index: 999; width: 44px; height: 44px; background: var(--vf-red); border: none; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; cursor: pointer; box-shadow: 0 4px 16px var(--vf-red-glow); transition: transform var(--vf-transition); }
        .vf-scroll-top:hover { transform: scale(1.1); }

        /* ── ANIMATIONS ── */
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fadeIn 0.8s ease forwards; }
        .animate-slide-up { animation: slideUp 0.9s ease forwards; }
        .animate-fade-in-delay { animation: fadeIn 1s ease 0.3s both; }
        .animate-fade-in-delay2 { animation: fadeIn 1s ease 0.6s both; }

        /* ── ABOUT SECTION SPECIFICS ── */
        #about .vf-section-alt { background: var(--vf-bg2); }
      `}</style>
    </div>
  );
}